/**
 * Centralized analytics module — PostHog + GA4 + Meta Pixel
 *
 * PostHog: product analytics + session recordings
 * GA4: SEO/marketing analytics + Google Search Console integration
 * Meta Pixel: Facebook/Instagram ad conversion tracking
 *
 * All are initialized only in production builds (import.meta.env.PROD).
 * All tracking calls are no-ops when not initialized.
 *
 * GDPR cookie consent:
 * - vanilla-cookieconsent (open source) handles the consent banner
 * - Analytics ONLY initialize after user grants consent
 * - If consent is declined, analytics are never loaded — GDPR wins
 */

import { loadPostHog, getPostHog } from './posthog-lazy'

// ─── Global typings ──────────────────────────────────────────────────────────

declare global {
  interface Window {
    dataLayer: unknown[]
    gtag: (...args: unknown[]) => void
    fbq: (...args: unknown[]) => void
  }
}

function gtag(...args: unknown[]) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag(...args)
  }
}

function fbq(...args: unknown[]) {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq(...args)
  }
}

// ─── Initialization ──────────────────────────────────────────────────────────

let _posthogReady = false
let _ga4Ready = false
let _metaReady = false

export async function initPostHog() {
  const key = import.meta.env.VITE_POSTHOG_KEY?.trim()
  const host = (import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com').trim()

  if (!key) {
    console.warn('[analytics] VITE_POSTHOG_KEY not set — PostHog disabled')
    return
  }

  const ph = await loadPostHog()
  if (!ph) return

  ph.init(key, {
    api_host: host,
    ui_host: 'https://eu.posthog.com',
    capture_pageview: true,
    autocapture: true,
    person_profiles: 'identified_only',
    advanced_disable_feature_flags: true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '[data-mask]',
    },
  })

  _posthogReady = true
}

export function initGA4() {
  // GA4 gtag.js is loaded statically in index.html.
  // Consent Mode v2 defaults (analytics_storage: "denied") gate data collection
  // until we call gtag("consent", "update", { analytics_storage: "granted" }).
  _ga4Ready = true

  // Update Consent Mode to allow analytics
  gtag('consent', 'update', {
    analytics_storage: 'granted',
  })
}

export function initMetaPixel() {
  // Meta Pixel base code is loaded statically in index.html.
  // This function marks it as ready for custom event tracking from the JS bundle.
  _metaReady = true

  // Update Consent Mode for ads
  gtag('consent', 'update', {
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
  })
}

// ─── Cookie consent banner (vanilla-cookieconsent) ──────────────────────────

/**
 * Initializes cookie consent banner and gates analytics on user consent.
 * - PostHog + GA4: require "analytics" category consent
 * - Meta Pixel: requires "marketing" category consent
 *
 * GDPR compliance: analytics NEVER load before active user consent.
 */
export async function initAnalyticsWithConsent() {
  if (!import.meta.env.PROD) return

  // Dynamic import CookieConsent to avoid bundling in initial chunk
  const CookieConsent = await import('vanilla-cookieconsent')
  await import('vanilla-cookieconsent/dist/cookieconsent.css')

  // Detect language from <html lang> or navigator
  const htmlLang = document.documentElement.lang || ''
  const isSpanish = htmlLang.startsWith('es') || navigator.language?.startsWith('es')

  CookieConsent.run({
    guiOptions: {
      consentModal: {
        layout: 'box inline',
        position: 'bottom left',
      },
      preferencesModal: {
        layout: 'box',
      },
    },

    categories: {
      necessary: {
        enabled: true,
        readOnly: true,
      },
      analytics: {
        autoClear: {
          cookies: [
            { name: /^_ga/ },
            { name: '_gid' },
            { name: /^ph_/ },
          ],
        },
      },
      marketing: {
        autoClear: {
          cookies: [
            { name: '_fbp' },
            { name: '_fbc' },
          ],
        },
      },
    },

    onFirstConsent: () => {
      applyConsentWith(CookieConsent)
    },

    onChange: () => {
      applyConsentWith(CookieConsent)
    },

    language: {
      default: isSpanish ? 'es' : 'en',
      translations: {
        en: {
          consentModal: {
            title: 'We use cookies',
            description: 'We use cookies to analyze site traffic and optimize your experience. You can choose which cookies to accept.',
            acceptAllBtn: 'Accept all',
            acceptNecessaryBtn: 'Reject all',
            showPreferencesBtn: 'Manage preferences',
          },
          preferencesModal: {
            title: 'Cookie preferences',
            acceptAllBtn: 'Accept all',
            acceptNecessaryBtn: 'Reject all',
            savePreferencesBtn: 'Save preferences',
            sections: [
              {
                title: 'Essential cookies',
                description: 'These cookies are necessary for the website to function properly and cannot be disabled.',
                linkedCategory: 'necessary',
              },
              {
                title: 'Analytics cookies',
                description: 'These cookies help us understand how visitors use our website, allowing us to improve the user experience.',
                linkedCategory: 'analytics',
              },
              {
                title: 'Marketing cookies',
                description: 'These cookies are used to track visitors across websites to display relevant advertisements.',
                linkedCategory: 'marketing',
              },
            ],
          },
        },
        es: {
          consentModal: {
            title: 'Usamos cookies',
            description: 'Usamos cookies para analizar el tráfico del sitio y optimizar tu experiencia. Puedes elegir qué cookies aceptar.',
            acceptAllBtn: 'Aceptar todas',
            acceptNecessaryBtn: 'Rechazar todas',
            showPreferencesBtn: 'Gestionar preferencias',
          },
          preferencesModal: {
            title: 'Preferencias de cookies',
            acceptAllBtn: 'Aceptar todas',
            acceptNecessaryBtn: 'Rechazar todas',
            savePreferencesBtn: 'Guardar preferencias',
            sections: [
              {
                title: 'Cookies esenciales',
                description: 'Estas cookies son necesarias para el funcionamiento del sitio web y no se pueden desactivar.',
                linkedCategory: 'necessary',
              },
              {
                title: 'Cookies analíticas',
                description: 'Estas cookies nos ayudan a entender cómo los visitantes usan nuestro sitio web, permitiéndonos mejorar la experiencia.',
                linkedCategory: 'analytics',
              },
              {
                title: 'Cookies de marketing',
                description: 'Estas cookies se utilizan para rastrear visitantes en sitios web y mostrar anuncios relevantes.',
                linkedCategory: 'marketing',
              },
            ],
          },
        },
      },
    },
  })
}

function applyConsentWith(CookieConsent: typeof import('vanilla-cookieconsent')) {
  const analyticsAccepted = CookieConsent.acceptedCategory('analytics')
  const marketingAccepted = CookieConsent.acceptedCategory('marketing')

  if (analyticsAccepted) {
    if (!_posthogReady) initPostHog()
    if (!_ga4Ready) initGA4()
  } else {
    if (_posthogReady) {
      getPostHog()?.opt_out_capturing()
      _posthogReady = false
    }
    _ga4Ready = false
    gtag('consent', 'update', { analytics_storage: 'denied' })
  }

  if (marketingAccepted) {
    if (!_metaReady) initMetaPixel()
  } else {
    _metaReady = false
    gtag('consent', 'update', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    })
  }
}

// ─── Identity ────────────────────────────────────────────────────────────────

export function identifyUser(
  userId: string,
  properties?: Record<string, unknown>,
) {
  if (_posthogReady) {
    getPostHog()?.identify(userId, properties)
  }
  if (_ga4Ready) {
    gtag('set', { user_id: userId })
    if (properties) {
      gtag('set', 'user_properties', properties)
    }
  }
}

export function resetAnalytics() {
  if (_posthogReady) {
    getPostHog()?.reset()
  }
  if (_ga4Ready) {
    gtag('set', { user_id: undefined })
  }
}

// ─── User properties ─────────────────────────────────────────────────────────

export function setUserProperties(props: Record<string, unknown>) {
  if (_posthogReady) {
    getPostHog()?.people.set(props)
  }
  if (_ga4Ready) {
    gtag('set', 'user_properties', props)
  }
}

export function setUserGroup(groupType: string, groupKey: string) {
  if (_posthogReady) {
    getPostHog()?.group(groupType, groupKey)
  }
}

// ─── Generic event tracking (PostHog + GA4 + Meta Pixel) ─────────────────────

export function trackEvent(name: string, properties?: Record<string, unknown>) {
  if (_posthogReady) {
    getPostHog()?.capture(name, properties)
  }
  if (_ga4Ready) {
    gtag('event', name, properties)
  }
  if (_metaReady) {
    fbq('trackCustom', name, properties)
  }
}

// ─── Typed event helpers ─────────────────────────────────────────────────────

// Landing page
export function trackCtaClicked(location: string, variant?: string) {
  trackEvent('cta_clicked', { location, variant })
}

export function trackPricingPlanClicked(plan: string) {
  trackEvent('pricing_plan_clicked', { plan })
}

// Auth
export function trackSignupStarted(method: 'email' | 'google') {
  trackEvent('signup_started', { method })
}

export function trackSignupCompleted(method: 'email' | 'google') {
  trackEvent('signup_completed', { method })
  if (_metaReady) fbq('track', 'Lead', { method })
}

export function trackLoginCompleted(method: 'email' | 'google') {
  trackEvent('login_completed', { method })
}

// Onboarding
export function trackOnboardingStepCompleted(
  step: number,
  data?: Record<string, unknown>,
) {
  trackEvent('onboarding_step_completed', { step, ...data })
}

export function trackOnboardingCompleted(totalLeadsFound: number) {
  trackEvent('onboarding_completed', { total_leads_found: totalLeadsFound })
  if (_metaReady) fbq('track', 'CompleteRegistration', { total_leads_found: totalLeadsFound })
}

export function trackWebsiteAnalyzed(domain: string) {
  trackEvent('website_analyzed', { domain })
}

// Campaigns
export function trackCampaignCreated(props: {
  mode: 'chat' | 'form'
  target_prospects?: number
  follow_ups?: number
}) {
  trackEvent('campaign_created', props)
}

export function trackCampaignActivated(campaignId: string) {
  trackEvent('campaign_activated', { campaign_id: campaignId })
}

export function trackCampaignPaused(campaignId: string) {
  trackEvent('campaign_paused', { campaign_id: campaignId })
}

export function trackCampaignDeleted(campaignId: string) {
  trackEvent('campaign_deleted', { campaign_id: campaignId })
}

export function trackCampaignSendNow(campaignId: string) {
  trackEvent('campaign_send_now', { campaign_id: campaignId })
}

export function trackCampaignSendLater(campaignId: string) {
  trackEvent('campaign_send_later', { campaign_id: campaignId })
}

export function trackModeSwitched(from: 'chat' | 'form', to: 'chat' | 'form') {
  trackEvent('mode_switched', { from, to })
}

// Email tasks
export function trackEmailTaskEdited(taskId: string) {
  trackEvent('email_task_edited', { task_id: taskId })
}

export function trackEmailPreviewGenerated() {
  trackEvent('email_preview_generated')
}

// Billing
export function trackUpgradeClicked(targetPlan: string, source: string) {
  trackEvent('upgrade_clicked', { target_plan: targetPlan, source })
}

export function trackCheckoutStarted(plan: string) {
  trackEvent('checkout_started', { plan })
  if (_metaReady) fbq('track', 'InitiateCheckout', { plan })
}

export function trackBillingPortalOpened() {
  trackEvent('billing_portal_opened')
}

// Landing engagement
export function trackVideoEngaged20s() {
  trackEvent('video_engaged_20s')
}

export function trackLandingEngaged20s() {
  trackEvent('landing_engaged_20s')
}
